/**
 * Paginated List Aspect Ratio Helper
 *
 * Helper class for maintaining consistent aspect ratios in card galleries
 * while using the Shopify theme editor. Ensures newly rendered product cards
 * respect the current (even unsaved) aspect-ratio setting.
 *
 * Features:
 * - Adaptive and fixed aspect ratio modes
 * - Image loading detection
 * - Ratio caching by product ID
 * - Batch DOM operations for performance
 *
 * This is particularly important in the theme editor where cards are dynamically
 * added/removed and need to maintain visual consistency.
 *
 * Usage:
 *   const helper = new PaginatedListAspectRatioHelper({
 *     templateCard: document.querySelector('[data-ref="cardGallery"]')
 *   });
 *   helper.processNewElements();
 */
export class PaginatedListAspectRatioHelper {
  /** @type {string | null} Stored image ratio setting from template card */
  #imageRatioSetting = null;

  /**
   * Aspect ratios aligned with the theme's standardized options.
   *
   * These values correspond to the aspect ratio options available
   * in the theme settings (square, portrait, landscape).
   *
   * @type {Object.<string, string>}
   * @readonly
   */
  #ASPECT_RATIOS = {
    square: '1',
    portrait: '0.8',
    landscape: '1.778',
  };

  /**
   * Creates a new aspect ratio helper instance.
   *
   * Only initializes in theme editor mode. Stores the template card's
   * aspect ratio setting for later use.
   *
   * @param {Object} options - Configuration options
   * @param {HTMLElement} options.templateCard - Source card used to read the current image ratio
   */
  constructor({ templateCard }) {
    // Only operate in theme editor mode
    if (!Shopify?.designMode) {
      return;
    }

    // Validate template card
    if (!templateCard || !(templateCard instanceof HTMLElement)) {
      return;
    }

    // Store the template card's ratio setting
    this.#storeImageRatioSettings(templateCard);
  }

  /**
   * Processes newly inserted cards and applies correct aspect ratios.
   *
   * Should be called after new cards are added to the DOM. Waits for
   * the DOM to settle before applying styles to avoid layout thrashing.
   */
  processNewElements() {
    // Only operate in theme editor mode
    if (!Shopify?.designMode) {
      return;
    }

    // Wait for DOM to settle before applying styles
    requestAnimationFrame(() => {
      // Apply adaptive or fixed ratio based on setting
      if (this.#imageRatioSetting === 'adapt') {
        this.#fixAdaptiveAspectRatios();
      } else {
        this.#applyFixedAspectRatio();
      }
    });
  }

  /**
   * Stores the template card's aspect ratio setting.
   *
   * Reads the data-image-ratio attribute from the template card
   * to determine which aspect ratio mode is active.
   *
   * @param {HTMLElement} templateCard - Card used to read the image ratio setting
   * @private
   */
  #storeImageRatioSettings(templateCard) {
    if (!templateCard || !(templateCard instanceof HTMLElement)) {
      return;
    }

    this.#imageRatioSetting = templateCard.getAttribute('data-image-ratio');
  }

  /**
   * Adjusts adaptive aspect ratios for newly added cards.
   *
   * In 'adapt' mode, each product uses its own image's intrinsic
   * aspect ratio. This method calculates and applies those ratios
   * to maintain visual consistency.
   *
   * @private
   */
  #fixAdaptiveAspectRatios() {
    // Find all unprocessed galleries
    const newCardGalleries = this.#getUnprocessedGalleries();
    if (!newCardGalleries || newCardGalleries.length === 0) {
      return;
    }

    // Cache ratios by product ID to avoid recalculating
    const productRatioCache = new Map();

    // Process each gallery
    newCardGalleries.forEach((gallery) => {
      if (!(gallery instanceof HTMLElement)) {
        return;
      }

      // Check if we've already calculated this product's ratio
      const productId = gallery.getAttribute('data-product-id');
      if (productId && productRatioCache.has(productId)) {
        // Use cached ratio
        const cachedRatio = productRatioCache.get(productId);
        if (cachedRatio && typeof cachedRatio === 'string') {
          this.#applyAspectRatioToGallery(gallery, cachedRatio);
        }
        return;
      }

      // Find the image element
      const image = gallery.querySelector('img');
      if (!image || !(image instanceof HTMLImageElement)) {
        // Default to square if no image found
        this.#applyAspectRatioToGallery(gallery, '1');
        return;
      }

      /**
       * Loads the image and calculates its aspect ratio.
       *
       * Once the image dimensions are available, calculates a safe
       * aspect ratio and applies it to the gallery.
       */
      const loadAndSetRatio = () => {
        try {
          // Exit if image dimensions aren't available
          if (!image.naturalWidth || !image.naturalHeight || image.naturalWidth <= 0 || image.naturalHeight <= 0) {
            return;
          }

          // Calculate safe aspect ratio
          const imageRatio = this.#getSafeImageAspectRatio(image.naturalWidth, image.naturalHeight);

          // Validate ratio
          if (!imageRatio || typeof imageRatio !== 'string') {
            return;
          }

          // Cache ratio if product ID is available
          if (productId && typeof productId === 'string') {
            productRatioCache.set(productId, imageRatio);
          }

          // Apply ratio to gallery
          this.#applyAspectRatioToGallery(gallery, imageRatio);
        } catch (error) {
          console.warn('PaginatedListAspectRatioHelper: Failed to load and set ratio', error); // eslint-disable-line no-console
        }
      };

      // If image is already loaded, calculate immediately
      if (image.complete && image.naturalWidth > 0 && image.naturalHeight > 0) {
        loadAndSetRatio();
      } else {
        // Otherwise wait for image to load
        image.addEventListener('load', loadAndSetRatio, { once: true });
      }
    });
  }

  /**
   * Applies a fixed aspect ratio to all relevant gallery/media containers.
   *
   * Used for non-adaptive modes (square, portrait, landscape) where
   * all cards should have the same aspect ratio regardless of their
   * image dimensions.
   *
   * @private
   */
  #applyFixedAspectRatio() {
    // Exit if no ratio setting
    if (!this.#imageRatioSetting || typeof this.#imageRatioSetting !== 'string') {
      return;
    }

    // Get the numeric aspect ratio value
    const aspectRatio = this.#getAspectRatioValue(this.#imageRatioSetting);
    if (!aspectRatio || typeof aspectRatio !== 'string') {
      return;
    }

    // Find all unprocessed galleries
    const newCardGalleries = this.#getUnprocessedGalleries();
    if (!newCardGalleries || newCardGalleries.length === 0) {
      return;
    }

    // Batch DOM mutations for better performance
    requestAnimationFrame(() => {
      newCardGalleries.forEach((gallery) => {
        if (!(gallery instanceof HTMLElement)) {
          return;
        }

        // Apply the fixed ratio
        this.#applyAspectRatioToGallery(gallery, aspectRatio);
      });
    });
  }

  /**
   * Computes a normalized aspect ratio from image dimensions.
   *
   * Constrains extreme values and formats to a stable precision
   * to prevent layout issues with very wide or very tall images.
   *
   * @param {number} width - Image width in pixels
   * @param {number} height - Image height in pixels
   * @returns {string} Normalized aspect ratio as a string (e.g., "1.500")
   * @private
   */
  #getSafeImageAspectRatio(width, height) {
    // Validate inputs
    if (
      typeof width !== 'number' ||
      typeof height !== 'number' ||
      !isFinite(width) ||
      !isFinite(height) ||
      width <= 0 ||
      height <= 0
    ) {
      return '1'; // Default to square
    }

    // Calculate raw ratio
    const rawRatio = width / height;

    // Constrain to reasonable range (0.1 to 10) and format to 3 decimal places
    const constrainedRatio = Math.max(0.1, Math.min(10, rawRatio));
    return constrainedRatio.toFixed(3);
  }

  /**
   * Resolves the numeric aspect ratio value for a setting key.
   *
   * Maps setting names (square, portrait, landscape) to their
   * numeric aspect ratio values.
   *
   * @param {string} ratioSetting - The ratio setting key
   * @returns {string | null} The numeric aspect ratio value, or null if invalid
   * @private
   */
  #getAspectRatioValue(ratioSetting) {
    if (!ratioSetting || typeof ratioSetting !== 'string') {
      return null;
    }

    return this.#ASPECT_RATIOS[ratioSetting] || null;
  }

  /**
   * Applies an aspect ratio to a gallery and its media containers.
   *
   * Sets CSS custom properties and inline styles to enforce the
   * aspect ratio on both the gallery and its media containers.
   *
   * @param {HTMLElement} gallery - The gallery element to style
   * @param {string} aspectRatio - The aspect ratio value to apply
   * @private
   */
  #applyAspectRatioToGallery(gallery, aspectRatio) {
    if (!(gallery instanceof HTMLElement)) {
      return;
    }

    if (!aspectRatio || typeof aspectRatio !== 'string') {
      return;
    }

    // Set CSS custom property on gallery
    gallery.style.setProperty('--gallery-aspect-ratio', aspectRatio);

    // Apply aspect ratio to all media containers within the gallery
    const mediaContainers = gallery.querySelectorAll('.product-media-container');
    mediaContainers.forEach((container) => {
      if (container instanceof HTMLElement) {
        container.style.aspectRatio = aspectRatio;
      }
    });

    // Mark gallery as processed to avoid duplicate work
    this.#markAsProcessed(gallery);
  }

  /**
   * Selects all galleries that haven't been processed yet.
   *
   * Uses a data attribute to track which galleries have already
   * had their aspect ratios applied.
   *
   * @returns {NodeListOf<Element>} NodeList of unprocessed gallery elements
   * @private
   */
  #getUnprocessedGalleries() {
    return document.querySelectorAll('.card-gallery:not([data-aspect-ratio-applied])');
  }

  /**
   * Flags a gallery as processed to avoid duplicate work.
   *
   * Sets a data attribute that prevents the gallery from being
   * processed again in future calls.
   *
   * @param {HTMLElement} gallery - The gallery element to mark
   * @private
   */
  #markAsProcessed(gallery) {
    if (!(gallery instanceof HTMLElement)) {
      return;
    }

    // Set attribute to mark as processed
    gallery.setAttribute('data-aspect-ratio-applied', 'true');
  }
}
